import { ArrowRight, ChevronRight, Leaf, Lightbulb, Lock, ShoppingBasket } from "lucide-react";
import { cn } from "@/lib/utils";

const KINDS = {
  // The Complete Meal Plan's premium value: one protein feature (the High-Protein Companion /
  // Protein Engine) surfaced contextually, and the Grocery Engine. Healthy tips are part of the plan.
  protein: {
    icon: Leaf,
    label: "Protein Engine",
    lockedTitle: "Protein guidance for this meal",
    lockedBody:
      "The High-Protein Companion tells you exactly what to add to this meal to hit your protein target for the day.",
  },
  grocery: {
    icon: ShoppingBasket,
    label: "Grocery Guide",
    lockedTitle: "This week's grocery planning",
    lockedBody:
      "The Grocery Guide turns the week ahead into one shopping list, with quantities for your household.",
  },
  tip: { icon: Lightbulb, label: "Healthy tip" },
};

/** Complete Meal Plan guidance as a contextual feature card; healthy tips as a margin note. */
export const BundleNote = ({ kind = "tip", text, label: labelOverride, locked = false, upgradeUrl, upgradeLabel, actionLabel, onAction, testId }) => {
  const { icon: Icon, label: defaultLabel, lockedTitle, lockedBody } = KINDS[kind] ?? KINDS.tip;
  // A plan may name the feature itself (the Healthy plans call it Protein Boost).
  const label = labelOverride ?? defaultLabel;
  const id = testId ?? `bundle-note-${kind}`;

  if (locked) {
    return (
      <aside
        data-testid={id}
        className="flex gap-3.5 rounded-[16px] border border-dashed border-brand-teal/25 bg-brand-mintSoft p-4"
      >
        <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-white text-brand-tealSoft shadow-[0_1px_2px_rgba(15,46,44,0.08)]">
          <Lock className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-brand-orangeInk">{label}</p>
          <p className="mt-1 font-display text-[14.5px] font-bold leading-snug text-brand-teal">
            {lockedTitle}
          </p>
          <p className="mt-1.5 text-[13px] leading-[1.5] text-brand-teal/60">{lockedBody}</p>
          {upgradeUrl && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-brand-teal/40">
                Included with the Complete Meal Plan
              </span>
              <a
                href={upgradeUrl}
                target="_blank"
                rel="noopener"
                data-testid={`bundle-note-upgrade-${kind}`}
                className="ps-tap inline-flex items-center gap-1 text-[12px] font-bold text-brand-orangeInk hover:underline"
              >
                {upgradeLabel ?? "Unlock"} <ChevronRight className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-testid={id}
      className={cn(
        "flex items-start gap-3 rounded-r-[16px] border-l-4 py-4 pl-4 pr-5",
        kind === "tip" ? "border-brand-orange bg-brand-peach" : "border-brand-green bg-brand-green/[0.07]"
      )}
    >
      <span className={cn("mt-0.5 shrink-0", kind === "tip" ? "text-brand-orangeInk" : "text-brand-green")}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-brand-orangeInk">{label}</p>
        <p className="mt-1 text-[13.5px] font-medium leading-[1.55] text-brand-teal">{text}</p>
        {actionLabel && onAction && (
          <button
            data-testid={`bundle-note-action-${kind}`}
            onClick={onAction}
            className="ps-tap mt-2 inline-flex min-h-[38px] items-center gap-1.5 text-[13px] font-bold text-brand-teal underline decoration-brand-teal/25 underline-offset-4 hover:text-brand-orangeInk hover:decoration-brand-orange/50"
          >
            {actionLabel} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </aside>
  );
};
